import { Avatar } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkPaletteSection, CkQuickAction } from "@NP-Webber/crm-kit/dist/next/index.js";
import { nextApi, type SearchSectionKey } from "../../api/nextApi";
import { helpPaletteSection } from "../help/HelpCenter";
import { RELATED_SETTINGS } from "../related/relatedCatalog";
import { matchesQuery, resolveSetting } from "../related/resolveSetting";
import { pushRecent } from "../related/relatedStore";
import { NAV_ICONS, NAV_MODULES, navModuleOf, type FlatNavItem } from "../shell/navConfig";
import { openAssistant, openAutomations, openDesignTour, openHelpCenter, openRelatedSettings, openToday } from "../events";

/**
 * The Ctrl+K palette — one search box for records (server: customers, users, tasks, forms…),
 * actions ("לקוח חדש"), screens, settings and help guides.
 */
const RECORD_ICONS: Record<SearchSectionKey, string> = {
  customers: "users",
  users: "user",
  tasks: "task",
  forms: "note",
  categories: "folder",
  funds: "wallet",
  suppliers: "box",
  meetings: "calendar",
};

/** "מסך · כספים" — the top-bar module the screen lives in */
function screenSub(navKey: string): string {
  const module = NAV_MODULES.find((m) => m.key === navModuleOf(navKey));
  return module ? `מסך · ${module.label}` : "מסך";
}

export async function buildPaletteResults(input: {
  query: string;
  nav: FlatNavItem[];
  quickActions: CkQuickAction[];
  customersLabel: string;
  can: (navKey: string) => boolean;
  /** the AI assistant is available (ai.use + provider configured) */
  assistant?: boolean;
  navigate: (to: string, opts?: { state?: unknown }) => void;
}): Promise<CkPaletteSection[]> {
  const q = input.query.trim();

  // server search runs while the local sections are built
  const recordsP =
    q.length >= 2
      ? nextApi.search(q).catch(() => [])
      : Promise.resolve([]);

  const actions = [
    input.assistant && q.length >= 2
      ? { key: "a-ai", label: `לשאול את עוזר ה-AI: «${q}»`, icon: "sparkle", kbd: "Ctrl+J", onSelect: () => openAssistant(q) }
      : null,
    ...input.quickActions.map((a) => ({ key: `a-${a.key}`, label: `${a.label} — חדש`, icon: a.icon, kbd: a.kbd, onSelect: a.onClick })),
    { key: "a-today", label: "היום בתוכנה", icon: "sun", onSelect: openToday },
    { key: "a-related", label: "הגדרות קשורות למסך הזה", icon: "sliders", kbd: "Ctrl+,", onSelect: openRelatedSettings },
    input.can("automations") ? { key: "a-automations", label: "אוטומציות בדף זה", icon: "sparkle", onSelect: () => openAutomations() } : null,
    { key: "a-help", label: "מרכז הדרכה", icon: "help", kbd: "F1", onSelect: () => openHelpCenter() },
    { key: "a-tour", label: "סיור בעיצוב החדש", icon: "sparkle", onSelect: openDesignTour },
  ]
    .filter(Boolean)
    .filter((a) => !q || a!.key === "a-ai" || matchesQuery(String(a!.label), q)) as CkPaletteSection["items"];

  const screens = input.nav
    .filter((s) => !q || matchesQuery(s.label, q))
    .slice(0, q ? 8 : 6)
    .map((s) => ({
      key: `s-${s.key}`,
      label: s.label,
      icon: NAV_ICONS[s.key] || "list",
      sub: screenSub(s.key),
      onSelect: () => input.navigate(s.path),
    }));

  const settings = q
    ? RELATED_SETTINGS.map((s) => resolveSetting(s, input.nav))
        .filter((s): s is NonNullable<typeof s> => s !== null && Boolean(s.path))
        .filter((s) => matchesQuery(`${s.title} ${s.description} ${s.keywords || ""} ${s.group}`, q))
        .slice(0, 6)
        .map((s) => ({
          key: `rs-${s.id}`,
          label: s.title,
          icon: s.icon,
          sub: `הגדרה · ${s.group}`,
          onSelect: () => {
            pushRecent(s.id);
            input.navigate(`${s.path}${s.query ? `?${s.query}` : ""}`, { state: s.focus ? { relatedFocus: s.focus } : undefined });
          },
        }))
    : [];

  const records = await recordsP;
  const recordSections: CkPaletteSection[] = records.map((sec) => ({
    title: sec.key === "customers" ? input.customersLabel : sec.title,
    items: sec.items.map((it) => ({
      key: `r-${sec.key}-${it.id}`,
      label: it.label,
      sub: it.sub || undefined,
      leading: sec.key === "customers" || sec.key === "users" ? <Avatar text={it.label} size={26} /> : undefined,
      icon: RECORD_ICONS[sec.key],
      onSelect: () => input.navigate(it.path),
    })),
  }));

  const guides = helpPaletteSection(q);
  return [
    ...recordSections,
    { title: "פעולות", items: q ? actions : actions.slice(0, 4) },
    { title: "מסכים", items: screens },
    ...(settings.length ? [{ title: "הגדרות", items: settings }] : []),
    ...(guides ? [guides] : []),
  ];
}
